/**
 * Cognito hosted sign-in for the SPA: OAuth 2.0 authorization code grant with
 * PKCE (RFC 7636), for a public client with no secret.
 *
 * Flow: signIn() stores a random PKCE verifier and state in sessionStorage and
 * redirects to the hosted sign-in page (password + TOTP MFA). Cognito
 * redirects back with ?code&state; getSession() checks the state, exchanges
 * the code plus verifier for tokens at /oauth2/token, removes the code from
 * the URL, and keeps the tokens in sessionStorage (cleared when the tab
 * closes). Expired access tokens are renewed with the refresh token.
 *
 * Tokens are only decoded here (for sub, expiry, and display name), never
 * trusted: the AgentCore authorizer and the agent verify them server-side.
 */
import { AuthProvider, AuthSession } from './auth-gate';

export interface CognitoConfig {
  /** Hosted sign-in base URL, e.g. https://<prefix>.auth.us-east-1.amazoncognito.com */
  domainUrl: string;
  clientId: string;
  /** Must exactly match a callback URL registered on the app client. */
  redirectUri: string;
}

/** Minimal storage port (sessionStorage in the browser). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Browser hooks, injectable for tests. */
export interface BrowserPort {
  /** Current page URL. */
  location(): string;
  /** Navigate away (to sign-in or sign-out). */
  assign(url: string): void;
  /** Replace the current URL without navigating (drop ?code&state). */
  replaceUrl(url: string): void;
  fetch(url: string, init: RequestInit): Promise<Response>;
}

const KEY_VERIFIER = 'txhhsc.pkce.verifier';
const KEY_STATE = 'txhhsc.pkce.state';
const KEY_TOKENS = 'txhhsc.tokens';
const SCOPES = 'openid email profile';

interface StoredTokens {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
}

export function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Base64url-encode a UTF-8 string (e.g. a JWT for the WebSocket subprotocol). */
export function base64UrlEncodeString(s: string): string {
  return base64UrlEncode(new TextEncoder().encode(s));
}

function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

/** S256 code challenge for a PKCE verifier. */
export async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/** Decode (without verifying) a JWT payload. */
export function decodeJwt(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  if (!part) throw new Error('malformed token');
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
  const json = new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
  return JSON.parse(json) as Record<string, unknown>;
}

export class CognitoAuthProvider implements AuthProvider {
  constructor(
    private readonly config: CognitoConfig,
    private readonly store: KeyValueStore,
    private readonly browser: BrowserPort,
  ) {}

  private get base(): string {
    return this.config.domainUrl.replace(/\/$/, '');
  }

  /** Session from an OAuth callback, stored tokens, or a refresh; else null. */
  async getSession(): Promise<AuthSession | null> {
    const url = new URL(this.browser.location());
    const code = url.searchParams.get('code');
    const error = url.searchParams.get('error');
    if (code || error) {
      const state = url.searchParams.get('state');
      const expected = this.store.getItem(KEY_STATE);
      const verifier = this.store.getItem(KEY_VERIFIER);
      this.store.removeItem(KEY_STATE);
      this.store.removeItem(KEY_VERIFIER);
      // Drop the code from the address bar and history either way.
      for (const k of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(k);
      this.browser.replaceUrl(url.toString());
      if (error) throw new Error(`sign-in failed: ${url.searchParams.get('error_description') ?? error}`);
      if (!state || state !== expected || !verifier) throw new Error('sign-in state mismatch');
      const tokens = await this.tokenRequest({
        grant_type: 'authorization_code',
        code: code!,
        code_verifier: verifier,
        redirect_uri: this.config.redirectUri,
      });
      this.saveTokens(tokens);
      return this.toSession(tokens);
    }

    const stored = this.loadTokens();
    if (!stored) return null;
    const session = this.toSession(stored);
    if (session.expiresAt - 60_000 > Date.now()) return session;
    return this.refresh(stored);
  }

  /** Redirect to the hosted sign-in page; resolves only after navigation. */
  async signIn(): Promise<AuthSession> {
    const verifier = randomString(48);
    const state = randomString(16);
    this.store.setItem(KEY_VERIFIER, verifier);
    this.store.setItem(KEY_STATE, state);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: SCOPES,
      state,
      code_challenge: await codeChallenge(verifier),
      code_challenge_method: 'S256',
    });
    this.browser.assign(`${this.base}/oauth2/authorize?${params.toString()}`);
    return new Promise<AuthSession>(() => {
      /* the page is navigating away */
    });
  }

  /** Renew the access token with the refresh token (e.g. after expiry). */
  async refresh(stored: StoredTokens | null = this.loadTokens()): Promise<AuthSession | null> {
    if (!stored?.refreshToken) {
      this.store.removeItem(KEY_TOKENS);
      return null;
    }
    try {
      const tokens = await this.tokenRequest({
        grant_type: 'refresh_token',
        refresh_token: stored.refreshToken,
      });
      const merged = { ...tokens, refreshToken: tokens.refreshToken ?? stored.refreshToken };
      this.saveTokens(merged);
      return this.toSession(merged);
    } catch {
      this.store.removeItem(KEY_TOKENS);
      return null;
    }
  }

  /** Revoke the refresh token, clear local tokens, and end the hosted session. */
  async signOut(): Promise<void> {
    const stored = this.loadTokens();
    this.store.removeItem(KEY_TOKENS);
    if (stored?.refreshToken) {
      try {
        await this.browser.fetch(`${this.base}/oauth2/revoke`, {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ token: stored.refreshToken, client_id: this.config.clientId }).toString(),
        });
      } catch {
        // Best effort: local tokens are already cleared.
      }
    }
    const params = new URLSearchParams({ client_id: this.config.clientId, logout_uri: this.config.redirectUri });
    this.browser.assign(`${this.base}/logout?${params.toString()}`);
  }

  private async tokenRequest(fields: Record<string, string>): Promise<StoredTokens> {
    const res = await this.browser.fetch(`${this.base}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: this.config.clientId, ...fields }).toString(),
    });
    if (!res.ok) throw new Error(`token request failed (${res.status})`);
    const body = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string };
    if (!body.access_token) throw new Error('token response missing access_token');
    return { accessToken: body.access_token, idToken: body.id_token, refreshToken: body.refresh_token };
  }

  private toSession(tokens: StoredTokens): AuthSession {
    const access = decodeJwt(tokens.accessToken);
    let username = typeof access.username === 'string' ? access.username : undefined;
    if (tokens.idToken) {
      const id = decodeJwt(tokens.idToken);
      if (typeof id.email === 'string') username = id.email;
    }
    return {
      accessToken: tokens.accessToken,
      cognitoSub: String(access.sub ?? ''),
      username,
      expiresAt: Number(access.exp ?? 0) * 1000,
    };
  }

  private loadTokens(): StoredTokens | null {
    const raw = this.store.getItem(KEY_TOKENS);
    if (!raw) return null;
    try {
      const t = JSON.parse(raw) as StoredTokens;
      return t.accessToken ? t : null;
    } catch {
      return null;
    }
  }

  private saveTokens(tokens: StoredTokens): void {
    this.store.setItem(KEY_TOKENS, JSON.stringify(tokens));
  }
}
